<template>
    <div>
        <!-- Add session warning modal here -->
        <b-modal v-model="showSessionWarning" 
                 title="Session Expiring Soon" 
                 hide-footer
                 no-close-on-esc
                 no-close-on-backdrop
                 centered>
            <div class="session-warning-content">
                <b-alert variant="warning" show class="mb-3">
                    <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
                    Your session will expire in <strong>{{ minutesLeft }} minutes</strong>.
                </b-alert>
                
                <p class="mb-3">
                    Please save your work and log in again to continue your session.
                    Any unsaved work may be lost.
                </p>
                
                <div class="text-center">
                    <b-button-group>
                        <b-button variant="secondary" @click="dismissWarning">
                            Dismiss ({{ minutesLeft }}m left)
                        </b-button>
                        <b-button variant="primary" 
                                @click="extendSession" 
                                class="ml-2"
                                :disabled="isRefreshingToken">
                            <b-spinner small v-if="isRefreshingToken" class="mr-1"></b-spinner>
                            <b-icon icon="arrow-clockwise" class="mr-1" v-else></b-icon>
                            {{ isRefreshingToken ? 'Renewing...' : 'Renew Session' }}
                        </b-button>
                    </b-button-group>
                </div>
                
                <!-- Countdown timer -->
                <div class="mt-3 text-center">
                    <small class="text-muted" style="cursor: pointer;" @click="showSessionWarningManually" title="Click to show this warning again">
                        <b-icon icon="clock" class="mr-1"></b-icon>
                        Time remaining: {{ formatTime(secondsLeft) }}
                    </small>
                </div>
            </div>
        </b-modal>

        <b-navbar toggleable="sm" type="dark" style="background-color: #364050;">
            <b-navbar-brand href="/">FURNITURE MECCA</b-navbar-brand>
            <div class="nav-refresh-menu-container">
              <b-navbar-nav v-if="authenticated" class="d-block d-md-none">
                <b-nav-item @click="refresh_packet"><b-icon icon="cloud-download" v-b-tooltip.hover title="Refresh System"></b-icon></b-nav-item>
              </b-navbar-nav>
              <b-navbar-toggle target="nav-collapse">
                <template #default="{ expanded }">
                  <span v-if="!expanded">☰</span>
                  <span v-else>✕</span>
                </template>
              </b-navbar-toggle>
            </div>

            <b-collapse id="nav-collapse" is-nav>
              <!-- Right aligned nav items -->
              <b-navbar-nav class="ml-auto align-items-center">
                
                <!-- Session timer indicator (visible when close to expiry) -->
                <b-nav-item v-if="showTimerInNav" class="session-timer-nav-item" @click="showSessionWarningManually">
                    <b-badge variant="warning" pill class="session-timer-badge" style="cursor: pointer;" v-b-tooltip.hover title="Click to view session details">
                        <b-icon icon="clock" class="mr-1"></b-icon>
                        {{ formatTime(secondsLeft) }}
                    </b-badge>
                </b-nav-item>

                <!-- Refresh button on mobile (inside collapse) -->
                <b-nav-item v-if="authenticated" class="d-block d-md-none">
                  <b-icon icon="cloud-download" @click="refresh_packet" v-b-tooltip.hover title="Refresh System"></b-icon>
                </b-nav-item>

                <!-- User dropdown -->
                <b-nav-item-dropdown right v-if="authenticated" class="nav-user-dropdown">
                  <!-- Using 'button-content' slot -->
                  <template v-slot:button-content>
                    <div class="user-info-wrapper">
                      <!-- User Avatar/Image -->
                      <div class="nav-user-avatar">
                        <div v-if="getUserImage()" class="nav-avatar-image-container">
                          <img
                            :src="getUserImage()"
                            :alt="userDetails.fullname || userDetails.username"
                            class="nav-user-avatar-image"
                            @error="handleImageError"
                          />
                        </div>
                        <div v-else class="nav-avatar-placeholder">
                          {{ getInitials(userDetails.fullname || userDetails.username) }}
                        </div>
                      </div>
                      
                      <!-- User Details - Hidden on mobile -->
                      <div class="nav-user-details d-none d-md-flex">
                        <div class="nav-user-text">
                          <div class="nav-user-name">{{ userDetails.fullname || userDetails.username }}</div>
                          <div class="nav-user-role">
                            {{ getUserRoleLabel(userDetails.userrole) }}
                            <span v-if="userLocation()" class="nav-user-location"> | {{ userLocation().text }}</span>
                          </div>
                        </div>
                        <b-icon icon="chevron-down" class="nav-dropdown-chevron ml-2"></b-icon>
                      </div>
                    </div>
                  </template>
                  <b-dropdown-item @click="viewProfile" class="dropdown-item-custom">
                    <b-icon icon="person" class="mr-2"></b-icon>
                    My Profile
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item v-on:click="logout" class="dropdown-item-custom">
                    <b-icon icon="box-arrow-right" class="mr-2"></b-icon>
                    Logout
                  </b-dropdown-item>
                </b-nav-item-dropdown>
              </b-navbar-nav>
            </b-collapse>

            <!-- Refresh button on desktop (outside collapse) -->
            <b-navbar-nav v-if="authenticated" class="d-none d-md-block ml-2">
              <b-nav-item @click="refresh_packet" class="nav-refresh-btn">
                <b-icon icon="cloud-download" v-b-tooltip.hover title="Refresh System"></b-icon>
              </b-nav-item>
            </b-navbar-nav>
        </b-navbar>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    export default {
        name: "Navigation",
        props: [],
        data: function(){
            return {
                authenticated: null,
                userDetails: {},
                packet: null,
                isMenuOpen: false,
                
                // Session timer properties
                showSessionWarning: false,
                minutesLeft: 0,
                secondsLeft: 0,
                sessionCheckInterval: null,
                sessionTimerInterval: null,
                showTimerInNav: false,
                // Load warning state from localStorage on component creation
                warningAlreadyShown: localStorage.getItem('sessionWarningDismissed') === 'true',
                isRefreshingToken: false,
                lastTokenCheckTime: 0
            }
        },
        methods: {
            // Session timer methods - COMPLETELY REWRITTEN
            startSessionTimer() {
                // Clear any existing intervals
                this.stopSessionTimer();
                
                // Function to update timer
                const updateTimer = () => {
                    const token = localStorage.getItem('userToken');
                    if (!token || !this.authenticated) {
                        this.stopSessionTimer();
                        return;
                    }
                    
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        const currentTime = Math.floor(Date.now() / 1000);
                        this.secondsLeft = payload.exp - currentTime;
                        this.minutesLeft = Math.floor(this.secondsLeft / 60);
                        
                        // Update navbar timer visibility (15 minutes = 900 seconds)
                        this.showTimerInNav = (this.secondsLeft > 0 && this.secondsLeft <= 900);
                        
                        // Handle modal warning (show once when we hit 15 minutes)
                        if (this.secondsLeft > 0 && this.secondsLeft <= 900) {
                            // Show modal if we haven't shown it yet and we're exactly at 15 minutes
                            // But allow manual opening anytime by not preventing based on warningAlreadyShown
                            if (this.secondsLeft <= 900 && this.secondsLeft > 890) {
                                this.showSessionWarning = true;
                            }
                        } else {
                            this.showSessionWarning = false;
                            
                            // Reset warning flag if we have more than 16 minutes left
                            if (this.secondsLeft > 960) {
                                this.warningAlreadyShown = false;
                                localStorage.setItem('sessionWarningDismissed', 'false');
                            }
                        }
                        
                        // Auto-logout when expired
                        if (this.secondsLeft <= 0) {
                            this.handleSessionExpired();
                        }
                    } catch (error) {
                        console.error('Error updating timer:', error);
                        this.handleSessionExpired();
                    }
                };
                
                // Update every second for real-time countdown
                this.sessionTimerInterval = setInterval(updateTimer, 1000);
                
                // Also do a deeper check every 30 seconds
                this.sessionCheckInterval = setInterval(() => {
                    updateTimer();
                }, 30000);
                
                // Initial update
                updateTimer();
            },
            
            stopSessionTimer() {
                if (this.sessionCheckInterval) {
                    clearInterval(this.sessionCheckInterval);
                    this.sessionCheckInterval = null;
                }
                if (this.sessionTimerInterval) {
                    clearInterval(this.sessionTimerInterval);
                    this.sessionTimerInterval = null;
                }
            },

            showSessionWarningManually() {
                // Only show if session is active and not expired
                const token = localStorage.getItem('userToken');
                if (!token || !this.authenticated) return;
                
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const currentTime = Math.floor(Date.now() / 1000);
                    const secondsLeft = payload.exp - currentTime;
                    
                    // Only show warning if session is still active
                    if (secondsLeft > 0) {
                        this.showSessionWarning = true;
                        
                        // Update the countdown display
                        this.secondsLeft = secondsLeft;
                        this.minutesLeft = Math.floor(secondsLeft / 60);
                        
                        // Show a toast to indicate manual opening
                        // this.$bvToast.toast('Session warning displayed', {
                        //     title: 'Session Timer',
                        //     variant: 'info',
                        //     autoHideDelay: 2000,
                        //     toaster: 'b-toaster-top-right'
                        // });
                    } else {
                        // Session already expired
                        this.handleSessionExpired();
                    }
                } catch (error) {
                    console.error('Error checking token for manual warning:', error);
                }
            },
            
            handleSessionExpired() {
                this.stopSessionTimer();
                this.showSessionWarning = false;
                this.showTimerInNav = false;
                this.warningAlreadyShown = false;
                localStorage.removeItem('sessionWarningDismissed');
                
                // Show expired message
                this.$bvToast.toast(
                    'Your session has expired. Please log in again.',
                    { 
                        title: 'Session Expired', 
                        autoHideDelay: 5000, 
                        variant: 'warning',
                        noCloseButton: false
                    }
                );
                
                // Logout after short delay
                setTimeout(() => {
                    this.logout();
                }, 2000);
            },
            
            dismissWarning() {
                this.showSessionWarning = false;
                
                // Show toast reminder
                this.$bvToast.toast(
                    `Session will expire in ${this.minutesLeft} minutes. Remember to save your work.`,
                    { 
                        title: 'Session Reminder', 
                        autoHideDelay: 3000, 
                        variant: 'info',
                        noCloseButton: false
                    }
                );
            },
            
            extendSession() {
                // Refresh token
                this.refreshToken();
            },
            
            formatTime(seconds) {
                if (seconds <= 0) return 'Expired';
                
                const mins = Math.floor(seconds / 60);
                const secs = seconds % 60;
                
                if (mins > 0) {
                    return `${mins}:${secs.toString().padStart(2, '0')}`;
                }
                return `${secs}s`;
            },
            
            refreshToken() {
                // Show loading state on the button
                this.isRefreshingToken = true;
                
                Requestor.postAsyncP('/api/user/refresh-token/', {}, this, 
                    (response) => {
                        this.isRefreshingToken = false;
                        
                        if (response.data && response.data.token) {
                            // Update token in localStorage
                            localStorage.setItem('userToken', response.data.token);
                            
                            // Reset warning state since we have a new token
                            this.warningAlreadyShown = false;
                            localStorage.setItem('sessionWarningDismissed', 'false');
                            
                            // Close modal and hide timer
                            this.showSessionWarning = false;
                            this.showTimerInNav = false;
                            
                            // Show success message
                            this.$bvToast.toast('Session renewed for another hour!', {
                                title: 'Session Renewed',
                                variant: 'success',
                                autoHideDelay: 4000,
                                toaster: 'b-toaster-top-center'
                            });
                            
                            // Restart the session timer with new token
                            setTimeout(() => {
                                this.startSessionTimer();
                            }, 500);
                            
                        } else if (response.data.error) {
                            this.handleRefreshError(response.data.error);
                        }
                    },
                    (error) => {
                        this.isRefreshingToken = false;
                        console.error('Token refresh error:', error);
                        
                        let errorMessage = 'Could not renew session.';
                        
                        if (error.response) {
                            if (error.response.status === 401) {
                                errorMessage = 'Your session has expired. Please log in again.';
                            } else if (error.response.data && error.response.data.error) {
                                errorMessage = error.response.data.error;
                            }
                        }
                        
                        this.handleRefreshError(errorMessage);
                    }
                );
            },

            handleRefreshError(errorMessage) {
                // Show error
                this.$bvToast.toast(errorMessage, {
                    title: 'Session Renewal Failed',
                    variant: 'danger',
                    autoHideDelay: 5000
                });
                
                // If it's an auth error, offer to save work before logout
                if (errorMessage.includes('expired') || errorMessage.includes('Please log in')) {
                    // Show confirmation before logging out
                    this.$bvModal.msgBoxConfirm(
                        'Your session could not be renewed. Would you like to save your work before logging out?',
                        {
                            title: 'Session Expired',
                            size: 'sm',
                            buttonSize: 'sm',
                            okVariant: 'primary',
                            okTitle: 'Save and Logout',
                            cancelTitle: 'Logout Now',
                            footerClass: 'p-2',
                            hideHeaderClose: false,
                            centered: true
                        }
                    ).then(shouldSave => {
                        if (shouldSave) {
                            // Trigger save functionality if available
                            this.$root.$emit('save-work-before-logout');
                            
                            // Wait a moment for save to complete
                            setTimeout(() => {
                                this.logout();
                            }, 2000);
                        } else {
                            // Logout immediately
                            this.logout();
                        }
                    });
                }
            },
            
            // User Image Methods
            getUserImage() {
                if (!this.userDetails || !this.userDetails.properties || !this.userDetails.properties.image_url) {
                    return null;
                }

                var imageUrl = this.userDetails.properties.image_url;
                if (typeof imageUrl === 'string') {
                    imageUrl = imageUrl.trim();
                }

                if (!imageUrl || imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                    return null;
                }

                // Handle CORS proxy if needed
                if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return 'https://corsproxy.io/?' + encodeURIComponent(imageUrl);
                }

                return imageUrl;
            },

            handleImageError(event) {
                console.warn('Image failed to load');
                event.target.style.display = 'none';
                // Replace with placeholder
                const parent = event.target.parentElement;
                if (parent) {
                    parent.innerHTML = `
                        <div class="nav-avatar-placeholder">
                            ${this.getInitials(this.userDetails.fullname || this.userDetails.username)}
                        </div>
                    `;
                }
            },

            getInitials(name) {
                if (!name) return '?';
                return name.split(' ')
                    .map(function(word) { return word[0]; })
                    .join('')
                    .toUpperCase()
                    .substring(0, 2);
            },

            getUserRoleLabel(role) {
                const roleMap = {
                    'admin': 'Admin',
                    'accountmanager': 'Account Manager',
                    'officeuser': 'Office User',
                    'officemanager': 'Office Manager',
                    'locationadmin': 'Location Admin',
                    'locationuser': 'Location User',
                    'locationstockperson': 'Location Stock Person',
                    'salesperson': 'Sales Person',
                    'warehouseuser': 'Warehouse User',
                    'warehousemanager': 'Warehouse Manager',
                    'accountsuser': 'Accounts User',
                    'housetruck': 'House Truck',
                    'deliverytruck': 'Delivery Truck'
                };
                return roleMap[role] || role;
            },

            // Profile Navigation
            viewProfile: function() {
                if (this.userDetails && this.userDetails.username) {
                    this.$router.push({
                        name: 'UserProfile',
                        params: { username: this.userDetails.username }
                    });
                } else {
                    this.$bvToast.toast(
                        "Unable to navigate to profile",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                }
            },

            userLocation() {
              if (!this.packet || !this.packet.locations || !(this.userDetails.properties && this.userDetails.properties.location_id)) return null;
              return this.packet.locations.find(
                (loc) => loc.value === this.userDetails.properties.location_id
              ) || null;
            },
            
            refresh_packet: function(e){
              if (e){
                e.preventDefault()
              }
              let path = "/api/user/get_tools_packet/";
                let data = {};
                let that = this;
                let s  = function(response){
                    if (response.data.result){
                        let locations = (response.data.result.locations || []).map(function(d){
                            return {value: d.id, text: d.name, short: d.properties.short, address: d.address, email: d.email, phone: d.phone, allowed: d.allowed}
                        });
                        locations.splice(0, 0, {value: null,  text: "All", short: "None", address: null, email: null, phone: null, allowed: []});

                        let locationsdict = locations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});


                        let sublocations = (response.data.result.sublocations || []).map(function(d){
                            return {value: d.id, text: d.name, location_id: d.location_id}
                        });
                        let sublocationsdict = sublocations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let locsubs = (response.data.result.locsubs || []);
                        let locsubsdict = locsubs.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let vendors = (response.data.result.vendors || []);
                        let vendorsdict = vendors.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let categories = (response.data.result.categories || []).map(function(d){
                            return {value: d.id, text: d.name}
                        });
                        let categoriesdict = categories.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let trucks = (response.data.result.trucks || []).map(function(d){
                            return {value: d.value, text: d.text}
                        });
                        let trucksdict = trucks.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

                        let packet = {
                            locations: locations,
                            locationsdict: locationsdict,
                            sublocations: sublocations,
                            sublocationsdict: sublocationsdict,
                            locsubs: locsubs,
                            locsubsdict: locsubsdict,
                            vendors: vendors,
                            vendorsdict: vendorsdict,
                            categories: categories,
                            categoriesdict: categoriesdict,
                            trucks: trucks,
                            trucksdict: trucksdict
                        }

                        localStorage.setItem("packet", JSON.stringify(packet));
                        that.packet = packet;
                    }
                    else {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                }

                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading locations.",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }

                Requestor.getAsyncP(path, data, this, s, f);
            },
            
            CheckAuthenticated: function(){
                if(localStorage.userToken){
                    this.authenticated = true;
                    let userDetails = JSON.parse(localStorage.userDetails);
                    if (userDetails){
                        this.userDetails = userDetails;
                    }
                    // Start session timer when authenticated
                    this.startSessionTimer();
                    
                    // Check if we should show warning on page load
                    this.checkWarningOnPageLoad();
                }
                else {
                    this.authenticated = null;
                    this.stopSessionTimer();
                }
            },
            
            checkWarningOnPageLoad() {
                const token = localStorage.getItem('userToken');
                if (!token) return;
                
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const currentTime = Math.floor(Date.now() / 1000);
                    const secondsLeft = payload.exp - currentTime;
                    
                    // If page was refreshed and we're within warning period,
                    // restore the timer state
                    if (secondsLeft > 0 && secondsLeft <= 900) {
                        this.showTimerInNav = true;
                        
                        // Check if warning was dismissed
                        const warningDismissed = localStorage.getItem('sessionWarningDismissed') === 'true';
                        this.warningAlreadyShown = warningDismissed;
                        
                        // Don't automatically show modal on page load to avoid annoying users
                        // The timer will appear in navbar instead
                    }
                } catch (error) {
                    console.error('Error checking token on page load:', error);
                }
            },
            
            logout: function(){
                this.stopSessionTimer();
                // Clear session warning state
                localStorage.removeItem('sessionWarningDismissed');
                localStorage.clear();
                this.authenticated = null;
                this.$router.push({"path": "/"})
            },
        },
        mounted: function(){
            this.CheckAuthenticated();
            let that = this;
            
            this.$root.$on("authenticated", (authenticated) => {
                this.authenticated = authenticated;
                if (authenticated) {
                    let userDetails = JSON.parse(localStorage.userDetails);
                    if (userDetails){
                        this.userDetails = userDetails;
                    }
                    // Start session timer
                    this.startSessionTimer();
                } else {
                    this.stopSessionTimer();
                }
            });
            
            try {
              that.packet = JSON.parse(localStorage.getItem("packet"));
            } catch (e) {
              console.warn("No packet in localStorage");
            }
        },
        
        beforeDestroy() {
            // Clean up intervals when component is destroyed
            this.stopSessionTimer();
        }
    }
</script>

<style scoped>
  /* Session warning modal styles */
  .session-warning-content {
    padding: 10px;
  }
  
  /* Session timer in navbar */
  .session-timer-nav-item {
    margin-right: 15px;
  }
  
  .session-timer-badge {
    font-size: 12px;
    padding: 5px 10px;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(255, 193, 7, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
  }
  
  /* Container for refresh and menu toggle */
  .nav-refresh-menu-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* User dropdown container */
  .nav-user-dropdown {
    margin-left: auto;
  }

  /* User info wrapper */
  .user-info-wrapper {
    display: flex;
    align-items: center;
    min-height: 40px;
  }

  /* Avatar styles */
  .nav-user-avatar {
    flex-shrink: 0;
  }

  .nav-avatar-image-container {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: border-color 0.2s ease;
  }

  .nav-avatar-image-container:hover {
    border-color: rgba(255, 255, 255, 0.6);
  }

  .nav-user-avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .nav-avatar-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  /* User details styles */
  .nav-user-details {
    display: flex;
    align-items: center;
    margin-left: 10px;
  }

  .nav-user-text {
    display: flex;
    flex-direction: column;
  }

  .nav-user-name {
    color: white;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .nav-user-role {
    color: rgba(255, 255, 255, 0.8);
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .nav-user-location {
    font-size: 10px;
  }

  .nav-dropdown-chevron {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    transition: transform 0.2s ease;
    margin-left: 8px;
  }

  .nav-user-dropdown.show .nav-dropdown-chevron {
    transform: rotate(180deg);
  }

  /* Dropdown custom styles */
  .dropdown-item-custom {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    color: #333;
    font-size: 14px;
    transition: background-color 0.2s ease;
  }

  .dropdown-item-custom:hover {
    background-color: #f8f9fa;
    color: #212529;
    text-decoration: none;
  }

  .dropdown-item-custom:active {
    background-color: #e9ecef;
  }

  /* Refresh button */
  .nav-refresh-btn {
    margin-left: 10px;
  }

  .nav-refresh-btn >>> .nav-link {
    padding: 8px;
    color: rgba(255, 255, 255, 0.8);
    transition: color 0.2s ease;
  }

  .nav-refresh-btn >>> .nav-link:hover {
    color: white;
  }

  /* Fix for dropdown alignment */
  .nav-item.dropdown >>> .dropdown-toggle {
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
  }

  .nav-item.dropdown >>> .dropdown-toggle::after {
    display: none !important;
  }

  /* Mobile specific styles */
  @media (max-width: 767.98px) {
    .session-timer-nav-item {
      margin-right: 10px;
    }
    
    .session-timer-badge {
      font-size: 10px;
      padding: 4px 8px;
    }
    
    .nav-user-avatar {
      margin-right: 0;
    }
    
    .nav-avatar-image-container,
    .nav-avatar-placeholder {
      width: 32px;
      height: 32px;
    }
    
    .user-info-wrapper {
      padding: 4px 0;
    }
    
    /* Ensure dropdown menu aligns properly on mobile */
    .nav-user-dropdown >>> .dropdown-menu {
      right: 0 !important;
      left: auto !important;
      margin-top: 5px;
    }
  }

  /* Desktop specific styles */
  @media (min-width: 768px) {
    .user-info-wrapper {
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }
    
    .user-info-wrapper:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    .nav-user-details {
      margin-left: 12px;
    }
  }

  /* Ensure navbar items align properly */
  .navbar-nav.ml-auto {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .navbar-nav.ml-auto >>> .nav-item {
    display: flex;
    align-items: center;
  }
</style>


// WEBPACK FOOTER //
// src/components/globals/Navigation.vue